/**
 * Reload current page
 */
export const reloadPage = () => {
    window.location.reload();
};
/**
 * Takes base64 string of an image and returns image type and raw data
 *
 * @param base64 - raw base64 image data
 *
 * @returns object that contains the `type` (png,jpeg...) and the `data` as an object
 */
export const getImageData = (base64) => {
    if (typeof base64 === 'string') {
        const baseSplit = base64.split(';base64,');
        if (baseSplit.length === 2) {
            const type = baseSplit[0].split('data:')[1];
            const data = baseSplit[1];
            if (type && type.indexOf('image/') > -1 && data) {
                return { type, data };
            }
        }
    }
    return {
        type: null,
        data: null,
    };
};
/**
 * Get the prefix of the path (first route)
 *
 * @param path - the path from location.pathName
 */
export const getPathPrefix = (path) => {
    return path.toLowerCase().split('/')[1];
};
/**
 * Get the App ID based on the path name of the URL
 *
 * @param path - the path from location.pathName
 */
export const getAppIdFromPath = (path) => {
    const validIds = ['files', 'packages', 'activity', 'automation', 'admin', 'help', 'settings', 'public', 'faspio'];
    const pathCheck = getPathPrefix(path);
    if (validIds.indexOf(pathCheck) > -1) {
        return pathCheck;
    }
};
/**
 * Parse the URL query params (location.string or custom value)
 * If no `searchString` is passed in the window.location version will be used for parsing
 *
 * @param searchString - string to parse for query params.  Usually from `this.props.location.search`
 *
 * @returns an object containing the query params
 */
export const getQueryParams = (searchString) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const foundValues = {};
    searchString = typeof searchString === 'string' ? searchString : '';
    let stringToTest = searchString || window.location.search || '';
    stringToTest = stringToTest.replace('?', '');
    const searchParts = stringToTest.split('&');
    searchParts.forEach(param => {
        const key = param.split('=')[0];
        if (key && param.indexOf('=') > -1) {
            const value = param.substring(param.indexOf('=') + 1);
            if (foundValues[key]) {
                if (Array.isArray(foundValues[key])) {
                    foundValues[key].push(value);
                }
                else {
                    const current = foundValues[key];
                    foundValues[key] = [];
                    foundValues[key].push(current);
                    foundValues[key].push(value);
                }
            }
            else {
                foundValues[key] = value;
            }
        }
    });
    return foundValues;
};
/**
 * Create a safe query param string that can be used with React Router DOM
 *
 * @param params - the params to put together as a string
 *
 * @returns a string that can be passed to `this.props.history.push` to set query params
 */
export const getParamString = (params = {}) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const paramsToUse = (params && typeof params === 'object' ? params : {});
    const keys = Object.keys(paramsToUse);
    const pairsToAdd = [];
    let queryString = keys.length ? '?' : '';
    keys.forEach(key => {
        if (Array.isArray(paramsToUse[key])) {
            paramsToUse[key].forEach((value) => {
                pairsToAdd.push({ key, value });
            });
        }
        else {
            pairsToAdd.push({ key, value: paramsToUse[key] });
        }
    });
    pairsToAdd.forEach((item, index) => {
        queryString += `${item.key}=${item.value}${index < (pairsToAdd.length - 1) ? '&' : ''}`;
    });
    return queryString;
};
/**
 * Retrieve an environment variable from the React app
 *
 * @param environmentVariable - the variable key to use for retrieving an environment variable
 */
export const getEnvironmentVar = (environmentVariable) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const envVar = {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (window.asperaBaseEnvironment && typeof window.asperaBaseEnvironment === 'object')
        Object.assign(envVar, window.asperaBaseEnvironment);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (window.asperaEnvironment && typeof window.asperaEnvironment === 'object')
        Object.assign(envVar, window.asperaEnvironment);
    return envVar[environmentVariable];
};
/**
 * Set an environment variable from the React app
 *
 * @param environmentVariable - the variable key to use for retrieving an environment variable
 * @param value - the value to set the environment variable to
 */
export const setEnvironmentVar = (environmentVariable, value) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const envVar = window.asperaEnvironment;
    if (environmentVariable && value && typeof envVar === 'object') {
        envVar[environmentVariable] = value;
    }
};
/**
 * Create a random ID to use for DOM unique values.
 * Mostly used for places where React map is used and key is required.
 * Will always start with a letter as HTML ID must begin with letter
 */
export const getRandomId = () => {
    const partialRandom = () => {
        return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
    };
    return `a${partialRandom()}-${partialRandom()}-${partialRandom()}-${partialRandom()}`;
};
/**
 * Function used to create a unique encryption key based off text
 *
 * @param text - text to encrypt
 * @param shift - encryption shift (defaults to 69)
 *
 * @returns encrypted string
 */
export const clientEncrypt = (text, shift = 69) => {
    const newChars = [];
    const textArray = text.split('');
    textArray.forEach((element, index) => {
        let code = (element.charCodeAt(0) + shift) % 122;
        if (code < 33) {
            code = 35 + ((index * 35) % 87);
        }
        newChars.push(String.fromCharCode(code) || '#');
    });
    return newChars.join('');
};
/**
 * Convert an irritable into an array. This is useful for taking Maps and making Arrays (Map.values() Map.keys())
 *
 * @param iterable - an iterable to take and convert to an array
 *
 * @returns an array containing the iterable values
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const iterableToArray = (iterable) => {
    const newArray = [];
    (function addItem() {
        const value = iterable.next();
        if (!value.done) {
            newArray.push(value.value);
            addItem();
        }
    }());
    return newArray;
};
/**
 * Create initials from a given string.
 * This will return the first letter from the first two words.
 * @param testString - the string to create initials based off of
 *
 * @returns the initials, which are either 1 or 2 characters
 *
 * @example
 * ```javascript
 * createInitials('Test User') // Returns 'TU'
 * createInitials('Test') // Returns 'T'
 * createInitials('Test user') // Returns 'TU'
 * createInitials('') // Returns '?'
 * ```
 */
export const createInitials = (testString) => {
    if (testString) {
        testString = String(testString);
        const words = testString.split(' ');
        // eslint-disable-next-line no-useless-escape
        const invalidChars = /[\(\)\{\}\[\]\|\\\/~!@#$%^&*:;,"'`<>_-]/;
        if (words[0] && words[1] && words[0].charAt(0).match(invalidChars) === null && words[1].charAt(0).match(invalidChars) === null) {
            return `${words[0].toLocaleUpperCase().charAt(0)}${words[1].toLocaleUpperCase().charAt(0)}`;
        }
        else if (words[0] && words[0].charAt(0).match(invalidChars) === null) {
            return `${words[0].toLocaleUpperCase().charAt(0)}`;
        }
    }
    return '?';
};
/**
 * Takes string and returns whether it is a valid email address
 *
 * @param email - string to test if email or not
 *
 * @returns whether string is valid email or not
 */
export const isValidEmail = (email) => {
    if (!email || typeof email !== 'string') {
        return false;
    }
    const validEmail = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
    return validEmail.test(email);
};
/**
 * Take an object of key values and get their data in an array of `{key: item, value: item}`
 *
 * @param itemsToGet - Object of key: value pairs to turn get
 *
 * @returns an array of objects containing key and value.
 */
// eslint-disable-next-line
export const getKeyValuePairs = (itemsToGet) => {
    return Object.keys(itemsToGet).map(key => {
        return {
            key,
            value: itemsToGet[key],
        };
    });
};
/**
 * Clone an object and break all references of it and child objects for Native code.
 * @remarks used this rarely.  DO NOT USE ON CLASSES.
 *
 * @param objectToClone - the original object to clone
 *
 * @returns a new version of the object.
 */
// eslint-disable-next-line
export const deepCloneNative = (objectToClone) => {
    try {
        const objectString = JSON.stringify(objectToClone);
        return JSON.parse(objectString);
    }
    catch (_error) {
        return null;
    }
};
/**
 * Calculate the number of days between two dates.  Rounds to the nearest.
 *
 * @param dateOne - first date to compare
 * @param dateTwo - second date to compare.  If not set will use current date
 *
 * @returns number of days between dates.
 */
export const daysBetweenDates = (dateOne, dateTwo) => {
    const now = dateTwo || new Date();
    return Math.abs(Math.round((now.getTime() - dateOne.getTime()) / (1000 * 60 * 60 * 24)));
};
/**
 * Returns a boolean indicating if the current platform is Safari
 *
 * @returns whether the current platform is Apple Safari
 */
export const isSafari = () => {
    // eslint-disable-next-line
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && !window.MSStream;
};
/**
 * Returns a boolean indicating if the current platform is Firefox
 *
 * @returns whether the current platform is Mozilla Firefox
 */
export const isFirefox = () => {
    // eslint-disable-next-line
    return /Firefox/.test(navigator.userAgent) && !window.MSStream;
};
/**
 * Returns a boolean indicating if the current platform is iOS (Apple iPhone/iPad/iPod touch)
 *
 * @returns whether the current platform is an iOS device
 */
export const isIOS = () => {
    // eslint-disable-next-line
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};
/**
 * Returns a boolean indicating if the current platform is Android (mobile OS)
 *
 * @returns whether the current platform is an Android device
 */
export const isAndroid = () => {
    return /(android)/i.test(navigator.userAgent);
};
/**
 * Returns a boolean indicating if the current platform is a mobile device (that we support with an App version)
 *
 * @returns whether the current platform is a mobile device that we support with an App
 */
export const isMobile = () => {
    return isIOS() || isAndroid();
};
/** Helper to clear focus on anything. Useful for some buttons with tooltips due to bug in Carbon */
export const clearFocus = () => {
    var _a, _b;
    if (typeof ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.blur) === 'function') {
        (_b = document.activeElement) === null || _b === void 0 ? void 0 : _b.blur();
    }
};
/**
 * Get the filename from a file system path
 *
 * @param filePath string of path on the file system
 * @returns string of file name
 */
export const getFilenameFromPath = (filePath) => {
    if (!filePath || typeof filePath !== 'string') {
        return typeof filePath === 'string' ? filePath : '';
    }
    return filePath.replace(/[\\/]$/, '').split(/[\\/]/).pop() || filePath;
};
/**
 * Check if an element has or its parent has a class name
 *
 * @param element - the element from an event or something with parent reference
 * @param className - the class name to check upward
 *
 * @returns boolean indicating if any parent of a element (upward to root) has class name specified
 */
export const doesElementParentHaveClass = (element, className) => {
    let found = false;
    const checkParent = (parent) => {
        var _a;
        if ((_a = parent === null || parent === void 0 ? void 0 : parent.classList) === null || _a === void 0 ? void 0 : _a.contains(className)) {
            found = true;
        }
        else if (parent === null || parent === void 0 ? void 0 : parent.parentElement) {
            checkParent(parent.parentElement);
        }
    };
    checkParent(element);
    return found;
};
/**
 * Decode string
 *
 * @param value - the value to decode
 *
 * @returns decoded string
 */
export const safeDecode = (value) => {
    try {
        return decodeURIComponent(window.atob(value || ''));
    }
    catch (error) {
        console.error('safeDecode: failed to decode', error);
        return '';
    }
};
/**
 * Encode string
 *
 * @param value - the value to encode
 *
 * @returns encoded string
 */
export const safeEncode = (value) => {
    try {
        return window.btoa(encodeURIComponent(value || ''));
    }
    catch (error) {
        console.error('safeEncode: failed to encode', error);
        return '';
    }
};
/**
 * Takes a string of possible emails and tries to parse them into each email.
 * If email parsed is invalid it is dropped from the returned array
 *
 * @param emails string of potential emails
 * @returns array containing all valid emails
 */
export const parseValidEmailsFromString = (emails) => {
    if (typeof emails !== 'string') {
        return [];
    }
    emails = emails.replace(/(\r\n|\n|\r)/gm, ' ');
    emails = emails.replace(/\t/g, ' ');
    emails = emails.replace(/mailto:/g, '');
    emails = emails.replace(/\s+(?= )/g, ' ');
    emails = emails.replace(/,/g, '|');
    emails = emails.replace(/;/g, '|');
    emails = emails.replace(/\s/g, '|');
    return emails.split('|').filter(email => isValidEmail(email));
};
/** Remove sensitive data from strings */
export const deleteSensitiveInfo = (dataString) => {
    let data = dataString || '';
    data = data.replace('\\"', '"');
    data = data.replace(/"Authorization": "*.*"/gi, '"Authorization": "__REDACTED__"');
    data = data.replace(/"aspera-public-token": "*.*"/gi, '"aspera-public-token": "__REDACTED__"');
    data = data.replace(/"password": "*.*"/gi, '"password": "__REDACTED__"');
    data = data.replace(/"Password": "*.*"/gi, '"Password": "__REDACTED__"');
    data = data.replace(/"username": "*.*"/gi, '"username": "__REDACTED__"');
    data = data.replace(/"token": "*.*"/gi, '"username": "__REDACTED__"');
    return data;
};
/**
 * Capitalize the first letter
 *
 * @param text text to capitalize
 * @returns string with first letter capitalized
 */
export const capitalizeFirstLetter = (text) => {
    return text.charAt(0).toLocaleUpperCase() + text.slice(1);
};
const exports = {
    getImageData,
    getAppIdFromPath,
    getPathPrefix,
    getQueryParams,
    getParamString,
    getEnvironmentVar,
    setEnvironmentVar,
    getRandomId,
    clientEncrypt,
    iterableToArray,
    isValidEmail,
    getKeyValuePairs,
    daysBetweenDates,
    isIOS,
    isAndroid,
    isMobile,
    isSafari,
    clearFocus,
    getFilenameFromPath,
    doesElementParentHaveClass,
    safeDecode,
    safeEncode,
    deleteSensitiveInfo,
    capitalizeFirstLetter,
};
export default exports;
